import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

import clsx from 'clsx';

import { useStyles } from './Style';

import PopUpLegenda from '../Legenda/PopUpLegenda';
import { ATALHOS_TECLADO } from './AtalhosTeclado';

export const TOTAL_QUESTOES = 20;
export const OPCOES_RESPOSTA = ['A', 'B', 'C', 'D', 'E', 'M'];

const ATALHOS_RESPOSTA = { A: 'A', B: 'B', C: 'C', D: 'D', E: 'E', N: 'M' };

const labelOpcao = opcao => opcao === 'M' ? (
    <Tooltip title="Nulo/Branco — nenhuma marcação legível nesta questão" arrow>
        <span>N/B</span>
    </Tooltip>
) : opcao;

const FormularioMarcacoes = ({ value, onChange, sugestoes, showSugestoes, disabled, onValidChange, onTabUltimaQuestao, onSubmit }) => {
    const classes = useStyles();

    const radiosRef = useRef({});
    const [questaoFocada, setQuestaoFocada] = useState(0);
    const [focado, setFocado] = useState(false);

    const respostas = useMemo(() => {
        const base = Array.isArray(value) ? value : [];
        return Array.from({ length: TOTAL_QUESTOES }, (_, index) => base[index] || '');
    }, [value]);

    const respondidas = respostas.filter(Boolean).length;
    const valido = respondidas === TOTAL_QUESTOES;

    const validoAnterior = useRef(null);
    useEffect(() => {
        if (validoAnterior.current !== valido) {
            validoAnterior.current = valido;
            if (onValidChange) onValidChange(valido);
        }
    }, [valido, onValidChange]);

    const handleQuestaoChange = (index, resposta) => {
        const novas = [...respostas];
        novas[index] = resposta;
        if (onChange) onChange(novas);
    };

    const focarRadio = useCallback((questao, opcao) => {
        const input = radiosRef.current[`${questao}-${opcao}`];
        if (input && input.focus) input.focus();
    }, []);

    const focarQuestao = useCallback(questao => {
        focarRadio(questao, respostas[questao] || OPCOES_RESPOSTA[0]);
    }, [respostas, focarRadio]);

    const handleKeyDown = event => {
        if (disabled) return;
        if (event.altKey || event.ctrlKey || event.metaKey) return;

        if (event.key === 'Tab' && !event.shiftKey && questaoFocada === TOTAL_QUESTOES - 1 && valido && onTabUltimaQuestao) {
            event.preventDefault();
            onTabUltimaQuestao();
            return;
        }

        if (event.key === 'Enter' && valido && onSubmit) {
            event.preventDefault();
            onSubmit();
            return;
        }

        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();

            const passo = event.key === 'ArrowLeft' ? -1 : 1;
            const destino = Math.max(0, Math.min(TOTAL_QUESTOES - 1, questaoFocada + passo));

            setQuestaoFocada(destino);
            focarQuestao(destino);
            return;
        }

        const atalho = ATALHOS_RESPOSTA[event.key.toUpperCase()];

        if (atalho) {
            event.preventDefault();
            handleQuestaoChange(questaoFocada, atalho);
            focarRadio(questaoFocada, atalho);
        }
    };

    const handleFocus = () => setFocado(true);

    const handleBlur = event => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocado(false);
    };

    const pendentes = respostas.reduce((acc, resposta, index) => resposta ? acc : acc.concat(index + 1), []);

    return (
        <div className={classes.formulario}>
            <Grid container justify="space-between" alignItems="center">
                <Grid item>
                    <div className={classes.formularioTitulo}>
                        <Typography variant="subtitle2">Marcações</Typography>
                        <PopUpLegenda
                            title="Atalhos do teclado"
                            texto={ATALHOS_TECLADO}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        />
                    </div>
                </Grid>
                <Grid item>
                    <Typography variant="caption" color="textSecondary">
                        {respondidas}/{TOTAL_QUESTOES} respondidas
                    </Typography>
                </Grid>
            </Grid>

            <div
                className={classes.cartaoScroll}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
            >
                <table className={classes.cartao}>
                    <tbody>
                        <tr>
                            <th className={classes.cartaoCelulaLabel}>Q</th>
                            {respostas.map((_, index) => (
                                <th
                                    key={index}
                                    className={clsx(classes.cartaoCelulaNumero, {
                                        [classes.cartaoCelulaFocada]: focado && index === questaoFocada,
                                    })}
                                >
                                    {index + 1}
                                </th>
                            ))}
                        </tr>

                        {OPCOES_RESPOSTA.map(opcao => (
                            <tr key={opcao}>
                                <th className={classes.cartaoCelulaLabel}>{labelOpcao(opcao)}</th>
                                {respostas.map((resposta, index) => {
                                    const sugerida = Boolean(showSugestoes) && Array.isArray(sugestoes) && sugestoes[index] === opcao;

                                    return (
                                        <td
                                            key={index}
                                            className={clsx(classes.cartaoCelula, {
                                                [classes.cartaoCelulaSugerida]: sugerida,
                                                [classes.cartaoCelulaFocada]: focado && index === questaoFocada,
                                            })}
                                            onFocus={() => setQuestaoFocada(index)}
                                        >
                                            <Radio
                                                size="small"
                                                color="primary"
                                                className={classes.cartaoRadio}
                                                name={`questao_${index + 1}`}
                                                value={opcao}
                                                checked={resposta === opcao}
                                                onChange={() => handleQuestaoChange(index, opcao)}
                                                disabled={disabled}
                                                inputRef={el => { radiosRef.current[`${index}-${opcao}`] = el; }}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {!valido &&
                <Typography variant="caption" className={classes.pendentes}>
                    Questões Pendentes: {pendentes.join(', ')}
                </Typography>
            }
        </div>
    );
};

export default FormularioMarcacoes;
